import { useMemo, useState } from 'react'
import {
  App, Button, Card, DatePicker, Form, Input, Modal, Select, Space, Switch,
  Tag, Typography,
} from 'antd'
import { CalendarOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { pmSchedulesApi, pmTemplatesApi } from '@/features/maintenance/api'
import { machinesApi } from '@/features/masters/machines/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { PMSchedule, PMTemplate } from '@/shared/types/maintenance'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import AuditTrailDrawer from './components/AuditTrailDrawer'

const { Text } = Typography

const FREQUENCIES = ['DAILY', 'WEEKLY', 'FORTNIGHTLY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'ANNUAL']

/**
 * Spec §10/§11 — PM Calendar + Checklist Completion.
 * Manager generates schedules for a window (idempotent — Phase 1 service
 * skips duplicates). Technicians/supervisors complete the checklist:
 * abnormality_found triggers a follow-up WO. Defer requires a reason.
 */
export default function PMCalendarPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canUpdate = usePermission('maintenance.update')

  const [genOpen, setGenOpen] = useState(false)
  const [completing, setCompleting] = useState<PMSchedule | null>(null)
  const [deferring, setDeferring] = useState<PMSchedule | null>(null)
  const [auditEntityId, setAuditEntityId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [overdueOnly, setOverdueOnly] = useState(false)

  // ── Lists ─────────────────────────────────────────────────────────────
  const { data: schedules, isLoading } = useQuery({
    queryKey: ['pm-schedules', plantId, statusFilter, overdueOnly],
    enabled: !!plantId,
    queryFn: () => pmSchedulesApi.list({
      plant_id: plantId, status: statusFilter, overdue_only: overdueOnly, limit: 500,
    }),
  })

  const { data: templates } = useQuery({
    queryKey: ['pm-templates'],
    queryFn: () => pmTemplatesApi.list(),
  })
  const tmplById: Record<string, PMTemplate> = useMemo(() => {
    const m: Record<string, PMTemplate> = {}
    for (const t of templates ?? []) m[t.id] = t
    return m
  }, [templates])

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-maint', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []
  const machineLabel = (id: string) =>
    machines.find((m) => m.id === id)?.machine_id_str ?? id.slice(0, 8)

  // ── Generate ──────────────────────────────────────────────────────────
  const [genForm] = Form.useForm()
  const generateMutation = useMutation({
    mutationFn: (vals: { range: [dayjs.Dayjs, dayjs.Dayjs]; frequencies: string[] }) =>
      pmSchedulesApi.generate({
        plant_id: plantId,
        from_date: vals.range[0].format('YYYY-MM-DD'),
        to_date:   vals.range[1].format('YYYY-MM-DD'),
        frequencies: vals.frequencies,
      }),
    onSuccess: (r) => {
      message.success(`PM generation: ${r.created} created, ${r.skipped} already existed`)
      qc.invalidateQueries({ queryKey: ['pm-schedules'] })
      setGenOpen(false)
    },
    onError: () => message.error('Generation failed'),
  })

  const overdueMutation = useMutation({
    mutationFn: () => pmSchedulesApi.markOverdue(plantId),
    onSuccess: (r) => {
      message.success(`${r.flipped_to_overdue} schedule(s) marked OVERDUE`)
      qc.invalidateQueries({ queryKey: ['pm-schedules'] })
    },
  })

  // ── Complete (checklist) ──────────────────────────────────────────────
  const [completeForm] = Form.useForm()
  const completeMutation = useMutation({
    mutationFn: (vals: { remarks?: string; follow_up_action?: string }) => {
      const items = (completing && tmplById[completing.template_id ?? '']?.checklist_json) ?? []
      const results: Record<string, unknown> = {}
      let anyAbnormal = false
      for (const it of items) {
        const v = completeForm.getFieldValue(`item__${it.item}`)
        const remarks = completeForm.getFieldValue(`item__${it.item}__remarks`)
        results[it.item] = { value: v, remarks: remarks ?? '' }
        if (it.input_type === 'checkbox' && v === false) anyAbnormal = true
      }
      return pmSchedulesApi.complete(completing!.id, {
        checklist_results_json: results,
        abnormality_found: anyAbnormal,
        follow_up_action: anyAbnormal ? (vals.follow_up_action ?? null) : null,
        remarks: vals.remarks,
      })
    },
    onSuccess: (sch) => {
      message.success(
        sch.follow_up_wo_id
          ? `PM completed — abnormality found, follow-up WO created`
          : `PM completed`,
      )
      setCompleting(null)
      qc.invalidateQueries({ queryKey: ['pm-schedules'] })
    },
    onError: () => message.error('PM complete failed'),
  })

  // ── Defer ──────────────────────────────────────────────────────────────
  const [deferForm] = Form.useForm()
  const deferMutation = useMutation({
    mutationFn: (vals: { reason: string; new_date: dayjs.Dayjs }) =>
      pmSchedulesApi.defer(deferring!.id, {
        reason: vals.reason,
        new_date: vals.new_date.format('YYYY-MM-DD'),
      }),
    onSuccess: () => {
      message.success('PM deferred')
      qc.invalidateQueries({ queryKey: ['pm-schedules'] })
      setDeferring(null)
    },
    onError: () => message.error('Defer failed'),
  })

  // ── Columns ────────────────────────────────────────────────────────────
  const columns = [
    { title: 'Sch No', dataIndex: 'schedule_no', key: 'sch', width: 130 },
    {
      title: 'Planned', dataIndex: 'planned_date', key: 'planned', width: 130,
      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
    },
    {
      title: 'Machine', key: 'machine', width: 110,
      render: (_: unknown, r: PMSchedule) => machineLabel(r.machine_id),
    },
    {
      title: 'Template', key: 'tmpl',
      render: (_: unknown, r: PMSchedule) => r.template_id
        ? tmplById[r.template_id]?.template_name ?? r.template_id.slice(0, 8)
        : '—',
    },
    { title: 'Frequency', dataIndex: 'frequency', key: 'freq', width: 110 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (v: string, r: PMSchedule) => (
        <Space>
          <MaintenanceStatusBadge status={v} />
          {r.overdue_flag && <Tag color="red">OVERDUE</Tag>}
        </Space>
      ),
    },
    {
      title: 'Follow-up WO', key: 'fwo', width: 110,
      render: (_: unknown, r: PMSchedule) => r.follow_up_wo_id ? <Tag color="purple">YES</Tag> : '—',
    },
    {
      title: 'Actions', key: 'actions', width: 240,
      render: (_: unknown, r: PMSchedule) => (
        <Space size={4}>
          {r.status === 'PLANNED' && canUpdate && (
            <Button size="small" type="link" onClick={() => {
              completeForm.resetFields()
              setCompleting(r)
            }}>Complete</Button>
          )}
          {r.status === 'PLANNED' && canUpdate && (
            <Button size="small" type="link" onClick={() => {
              deferForm.resetFields()
              setDeferring(r)
            }}>Defer</Button>
          )}
          <Button size="small" type="link" onClick={() => setAuditEntityId(r.id)}>Audit</Button>
        </Space>
      ),
    },
  ]

  const completingTemplate = completing ? tmplById[completing.template_id ?? ''] : undefined

  return (
    <div>
      <PageHeader
        title="PM Calendar"
        subtitle="Generate, monitor and complete preventive maintenance jobs."
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => qc.invalidateQueries({ queryKey: ['pm-schedules'] })}>Refresh</Button>
            <Button icon={<ThunderboltOutlined />} onClick={() => overdueMutation.mutate()} loading={overdueMutation.isPending}>
              Recompute overdue
            </Button>
            {canUpdate && (
              <Button type="primary" icon={<CalendarOutlined />} onClick={() => {
                genForm.resetFields()
                genForm.setFieldsValue({
                  range: [dayjs(), dayjs().add(30, 'day')],
                  frequencies: ['DAILY', 'WEEKLY', 'MONTHLY'],
                })
                setGenOpen(true)
              }}>
                Generate schedules
              </Button>
            )}
          </Space>
        }
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select
            allowClear placeholder="Status filter" style={{ width: 200 }}
            options={['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE', 'DEFERRED', 'SKIPPED']
              .map((s) => ({ label: s, value: s }))}
            onChange={setStatusFilter}
          />
          <Switch checked={overdueOnly} onChange={setOverdueOnly} checkedChildren="Overdue only" unCheckedChildren="All" />
          <Text type="secondary">{schedules?.length ?? 0} schedule(s)</Text>
        </Space>
      </Card>

      <DataTable rowKey="id" columns={columns} dataSource={schedules} loading={isLoading} />

      {/* Generate */}
      <Modal
        title="Generate PM schedules"
        open={genOpen}
        onOk={() => genForm.validateFields().then((v) => generateMutation.mutate(v))}
        onCancel={() => setGenOpen(false)}
        confirmLoading={generateMutation.isPending}
      >
        <Form form={genForm} layout="vertical">
          <Form.Item name="range" label="Date range" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="frequencies" label="Frequencies"
            rules={[{ required: true, message: 'Pick at least one frequency' }]}>
            <Select
              mode="multiple"
              options={FREQUENCIES.map((f) => ({ label: f, value: f }))}
            />
          </Form.Item>
          <Text type="secondary">Idempotent — running twice for the same range will not create duplicates.</Text>
        </Form>
      </Modal>

      {/* Complete checklist */}
      <Modal
        title={`Complete PM ${completing?.schedule_no ?? ''}`}
        open={!!completing}
        onOk={() => completeForm.validateFields().then((v) => completeMutation.mutate(v))}
        onCancel={() => setCompleting(null)}
        confirmLoading={completeMutation.isPending}
        width={620}
      >
        <Form form={completeForm} layout="vertical">
          {completingTemplate ? (
            (completingTemplate.checklist_json ?? []).map((item) => (
              <Card size="small" style={{ marginBottom: 8 }} key={item.item}>
                <Form.Item
                  name={`item__${item.item}`}
                  label={item.item + (item.mandatory ? ' *' : '')}
                  rules={item.mandatory ? [{ required: true }] : undefined}
                  valuePropName={item.input_type === 'checkbox' ? 'checked' : undefined}
                  initialValue={item.input_type === 'checkbox' ? true : undefined}>
                  {item.input_type === 'checkbox' ? (
                    <Switch checkedChildren="OK" unCheckedChildren="NOT OK" />
                  ) : item.input_type === 'number' ? (
                    <Input type="number" />
                  ) : (
                    <Input />
                  )}
                </Form.Item>
                <Form.Item
                  name={`item__${item.item}__remarks`}
                  label="Remarks (required if Not OK)"
                  rules={[
                    {
                      validator: (_, v) => {
                        const result = completeForm.getFieldValue(`item__${item.item}`)
                        return item.input_type === 'checkbox' && result === false && !v
                          ? Promise.reject(new Error('Remarks needed when Not OK'))
                          : Promise.resolve()
                      },
                    },
                  ]}>
                  <Input.TextArea rows={1} />
                </Form.Item>
              </Card>
            ))
          ) : (
            <Text type="secondary">No checklist template attached. Enter remarks below.</Text>
          )}

          <Form.Item name="follow_up_action" label="Follow-up action (only if abnormality)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Any "Not OK" answer will create a follow-up work order automatically.
          </Text>
        </Form>
      </Modal>

      {/* Defer */}
      <Modal
        title={`Defer PM ${deferring?.schedule_no ?? ''}`}
        open={!!deferring}
        onOk={() => deferForm.validateFields().then((v) => deferMutation.mutate(v))}
        onCancel={() => setDeferring(null)}
        confirmLoading={deferMutation.isPending}>
        <Form form={deferForm} layout="vertical">
          <Form.Item name="reason" label="Reason (≥5 chars)" rules={[{ required: true }, { min: 5 }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="new_date" label="New planned date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <AuditTrailDrawer
        entityId={auditEntityId}
        title="PM Audit Trail"
        open={auditEntityId !== null}
        onClose={() => setAuditEntityId(null)}
      />
    </div>
  )
}
